import { onValue, push, ref, remove, update } from "firebase/database";
import { useEffect, useMemo, useRef, useState } from "react";
import { db } from "../Firebase";

const LEVELS = ["राष्ट्रीय स्तर", "राज्य स्तर", "संभाग स्तर", "जिला स्तर", "मंदिर स्तर"];

const LEVEL_COLORS = {
  "राष्ट्रीय स्तर": "danger",
  "राज्य स्तर": "warning",
  "संभाग स्तर": "primary",
  "जिला स्तर": "success",
  "मंदिर स्तर": "purple",
};

const EMPTY = {
  name: "", post: "", phone: "", email: "",
  level: "", state: "", division: "", district: "", temple: "", photo: "",
};

/* ── tiny toast ── */
let _toastTimer;
const useToast = () => {
  const [toast, setToast] = useState({ msg: "", type: "success", show: false });
  const fire = (msg, type = "success") => {
    clearTimeout(_toastTimer);
    setToast({ msg, type, show: true });
    _toastTimer = setTimeout(() => setToast(t => ({ ...t, show: false })), 2500);
  };
  return { toast, fire };
};

/* ── initials avatar ── */
const Avatar = ({ name, photo, size = 64 }) => {
  const [err, setErr] = useState(false);
  const letters = (name || "").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() || "जै";
  if (photo && !err)
    return <img src={photo} alt={name} onError={() => setErr(true)}
      style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", border: "2px solid #dee2e6" }} />;
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", background: "linear-gradient(135deg,#213F6D,#193264)",
      color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
      fontWeight: 800, fontSize: size * 0.32, flexShrink: 0,
    }}>{letters}</div>
  );
};

/* ── location label ── */
const locationOf = m =>
  [m.state, m.division, m.district, m.temple].filter(Boolean).join(" • ") || "राष्ट्रीय कार्यालय";

/* ══════════════════════════════════════════════
   MEMBER FORM (add / edit)
══════════════════════════════════════════════ */
const MemberForm = ({ initial, states, cities, blocks, mandirs, onSave, onCancel, saving, openImage }) => {
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const [photoPreview, setPhotoPreview] = useState(initial?.photo || "");
  const fileRef = useRef();

  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const filteredCities  = cities.filter(c => c.state === f.state);
  const filteredBlocks  = blocks.filter(b => b.city  === f.division);
  const filteredMandirs = mandirs.filter(m => m.district === f.district);

  const handlePhoto = e => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { setPhotoPreview(reader.result); set("photo", reader.result); };
    reader.readAsDataURL(file);
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (!f.name.trim() || !f.post.trim() || !f.phone.trim() || !f.level)
      return alert("कृपया सभी * वाले फ़ील्ड भरें।");
    onSave(f);
  };

  const field = (label, key, type = "text", req = false) => (
    <div className="mb-3">
      <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
        {label}{req && <span className="text-danger"> *</span>}
      </label>
      <input className="form-control form-control-sm" type={type} value={f[key]}
        onChange={e => set(key, e.target.value)} required={req} />
    </div>
  );

  return (
    <form onSubmit={handleSubmit}>
      <div className="row g-3">
        {/* Photo */}
        <div className="col-12 text-center mb-1">
          <div style={{ cursor: "pointer", display: "inline-block", position: 'relative' }}>
            <div onClick={() => fileRef.current.click()}>
              <Avatar name={f.name} photo={photoPreview || f.photo} size={110} />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 6 }}>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => fileRef.current.click()}>📷 बदलें</button>
              {(photoPreview || f.photo) && openImage && (
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openImage(photoPreview || f.photo)}>🔍 दिखाएँ</button>
              )}
            </div>
          </div>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={handlePhoto} />
        </div>

        <div className="col-md-6">{field("नाम", "name", "text", true)}</div>
        <div className="col-md-6">{field("पद / जिम्मेदारी", "post", "text", true)}</div>
        <div className="col-md-6">{field("मोबाइल", "phone", "tel", true)}</div>
        <div className="col-md-6">{field("ईमेल", "email", "email")}</div>

        {/* Level */}
        <div className="col-md-6">
          <label className="form-label fw-semibold" style={{ fontSize: 13 }}>स्तर <span className="text-danger">*</span></label>
          <select className="form-select form-select-sm" value={f.level}
            onChange={e => set("level", e.target.value)} required>
            <option value="">-- स्तर चुनें --</option>
            {LEVELS.map(l => <option key={l}>{l}</option>)}
          </select>
        </div>

        {/* State */}
        <div className="col-md-6">
          <label className="form-label fw-semibold" style={{ fontSize: 13 }}>राज्य</label>
          <select className="form-select form-select-sm" value={f.state}
            onChange={e => { set("state", e.target.value); set("division", ""); set("district", ""); set("temple", ""); }}>
            <option value="">-- राज्य चुनें --</option>
            {states.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
          </select>
        </div>

        {/* Division / City */}
        <div className="col-md-6">
          <label className="form-label fw-semibold" style={{ fontSize: 13 }}>संभाग / शहर</label>
          <select className="form-select form-select-sm" value={f.division}
            onChange={e => { set("division", e.target.value); set("district", ""); set("temple", ""); }}
            disabled={!f.state}>
            <option value="">-- संभाग चुनें --</option>
            {filteredCities.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
        </div>

        {/* District / Block */}
        <div className="col-md-6">
          <label className="form-label fw-semibold" style={{ fontSize: 13 }}>जिला / ब्लॉक</label>
          <select className="form-select form-select-sm" value={f.district}
            onChange={e => { set("district", e.target.value); set("temple", ""); }}
            disabled={!f.division}>
            <option value="">-- जिला चुनें --</option>
            {filteredBlocks.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
          </select>
        </div>

        {/* Temple / Org */}
        <div className="col-md-6">
          <label className="form-label fw-semibold" style={{ fontSize: 13 }}>मंदिर / संगठन</label>
          <select className="form-select form-select-sm" value={f.temple}
            onChange={e => set("temple", e.target.value)} disabled={!f.district}>
            <option value="">-- मंदिर चुनें --</option>
            {filteredMandirs.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
          </select>
        </div>
      </div>

      <div className="d-flex gap-2 mt-4">
        <button type="submit" className="btn btn-sm flex-fill text-white fw-bold"
          style={{ background: "#213F6D" }} disabled={saving}>
          {saving ? "सेव हो रहा है..." : "💾 सदस्य सेव करें"}
        </button>
        <button type="button" className="btn btn-sm btn-outline-secondary flex-fill" onClick={onCancel}>
          रद्द करें
        </button>
      </div>
    </form>
  );
};

/* ══════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════ */
const TeamAdmin = () => {
  const [members, setMembers]   = useState([]);
  const [states, setStates]     = useState([]);
  const [cities, setCities]     = useState([]);
  const [blocks, setBlocks]     = useState([]);
  const [mandirs, setMandirs]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [search, setSearch]     = useState("");
  const [filterLevel, setFilterLevel] = useState("");
  const [activeTab, setActiveTab]     = useState("list"); // list | add | report
  const [editMember, setEditMember]   = useState(null);
  const [showModal, setShowModal]     = useState(false);
  const { toast, fire } = useToast();
  const isAdmin = JSON.parse(localStorage.getItem('loggedUser') || 'null')?.role === 'Admin';
  const [activeView, setActiveView] = useState('dashboard'); // dashboard | members | new | reports | settings
  const [q, setQ] = useState({ ...EMPTY });
  const [imageViewer, setImageViewer] = useState(null);
  const qFileRef = useRef();

  /* ── Firebase listeners ── */
  useEffect(() => {
    const unsubs = [];
    const listen = (path, setter, transform = v => v) =>
      unsubs.push(onValue(ref(db, path), snap => {
        setter(snap.exists() ? transform(snap.val()) : []);
        setLoading(false);
      }));

    listen("team_members", setMembers, val =>
      Object.entries(val).map(([id, v]) => ({ id, ...v }))
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
    );
    listen("masters/states",    setStates,  val => Object.entries(val).map(([id, v]) => ({ id, ...v })));
    listen("masters/cities",    setCities,  val => Object.entries(val).map(([id, v]) => ({ id, ...v })));
    listen("masters/blocks",    setBlocks,  val => Object.entries(val).map(([id, v]) => ({ id, ...v })));
    listen("masters/othermasters", setMandirs, val => Object.entries(val).map(([id, v]) => ({ id, ...v })));

    return () => unsubs.forEach(u => u());
  }, []);

  /* ── filtered list ── */
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return members.filter(m =>
      (!filterLevel || m.level === filterLevel) &&
      (!q || [m.name, m.post, m.phone, m.email, locationOf(m)].join(" ").toLowerCase().includes(q))
    );
  }, [members, search, filterLevel]);

  /* ── stats ── */
  const stats = useMemo(() => {
    const count = l => members.filter(m => m.level === l).length;
    return {
      total: members.length,
      national: count("राष्ट्रीय स्तर"),
      state:    count("राज्य स्तर"),
      division: count("संभाग स्तर"),
      district: count("जिला स्तर"),
      temple:   count("मंदिर स्तर"),
      states:   new Set(members.map(m => m.state).filter(Boolean)).size,
    };
  }, [members]);

  /* ── save (add / edit) ── */
  const handleSave = async (data) => {
    setSaving(true);
    try {
      if (editMember) {
        await update(ref(db, `team_members/${editMember.id}`), { ...data, updatedAt: Date.now() });
        fire("सदस्य की जानकारी अपडेट हो गई ✅");
      } else {
        await push(ref(db, "team_members"), { ...data, createdAt: Date.now() });
        fire("नया सदस्य सफलतापूर्वक जोड़ दिया गया ✅");
      }
      setShowModal(false);
      setEditMember(null);
      setActiveTab("list");
    } catch (e) {
      fire("त्रुटि: " + e.message, "danger");
    }
    setSaving(false);
  };

  /* ── delete ── */
  const handleDelete = async (m) => {
    if (!window.confirm(`क्या आप "${m.name}" को हटाना चाहते हैं?`)) return;
    try {
      await remove(ref(db, `team_members/${m.id}`));
      fire("सदस्य हटा दिया गया 🗑️");
    } catch (e) {
      fire("त्रुटि: " + e.message, "danger");
    }
  };

  /* ── backup ── */
  const handleBackup = () => {
    const blob = new Blob([JSON.stringify(members, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `team-backup-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    fire("बैकअप डाउनलोड शुरू हुआ 💾");
  };

  /* ── open edit modal ── */
  const openEdit = (m) => { setEditMember(m); setShowModal(true); };
  const openAdd  = ()  => { setEditMember(null); setShowModal(true); };

  /* ══ RENDER ══ */
  const handleQuickChange = (k, v) => setQ(p => ({ ...p, [k]: v }));
  const handleQuickSubmit = async (e) => {
    e.preventDefault();
    if (!q.name.trim() || !q.post.trim() || !q.phone.trim() || !q.level) { fire('कृपया सभी * वाले फ़ील्ड भरें।', 'danger'); return; }
    setSaving(true);
    try {
      await push(ref(db, 'team_members'), { ...q, createdAt: Date.now() });
      setQ({ ...EMPTY });
      fire('नया सदस्य सफलतापूर्वक जोड़ दिया गया ✅');
      setActiveView('dashboard');
    } catch (e) {
      fire('त्रुटि: ' + e.message, 'danger');
    }
    setSaving(false);
  };

  const openImage = (src) => { if (!src) return; setImageViewer(src); };
  const closeImage = () => setImageViewer(null);

  const handleQPhoto = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { setQ(p => ({ ...p, photo: reader.result })); };
    reader.readAsDataURL(file);
  };

  return (
    <div style={{ minHeight: '100vh' }}>
      <style>{`
        /* Injected admin layout styles (adapted) */
        ${"*{box-sizing:border-box}body{margin:0;font-family:\"Noto Sans Devanagari\",\"Mangal\",Arial,sans-serif;background:#f7f8fb;color:#24262b}"}
        :root{--maroon:#213F6D;--maroon2:#193264;--gold:#d8a52a;--blue:#1675e8;--green:#1f9b49;--purple:#8138d7;--line:#e4e6eb}
        .app{min-height:100vh;display:flex}
        .sidebar{width:238px;flex:none;background:linear-gradient(180deg,#213F6D,#193264);color:#fff;padding:16px 10px;display:flex;flex-direction:column;position:sticky;top:0;height:100vh}
        .brand{display:flex;align-items:center;gap:10px;padding:5px 8px 18px;border-bottom:1px solid #ffffff25}
        .brand-logo{width:48px;height:48px;border-radius:50%;background:#fff;color:var(--maroon);display:grid;place-items:center;font-weight:900;border:2px solid var(--gold)}
        .brand b{font-size:21px}.brand small{display:block;color:#ffd9aa;font-size:11px}
        .navbtn{border:0;background:transparent;color:#fff;text-align:left;padding:12px 14px;margin:3px 0;border-radius:9px;font:inherit;cursor:pointer}
        .navbtn.active{background:var(--maroon2);box-shadow:inset 3px 0 #f0bd38}
        .quick{border:1px solid #e6a92b66;border-radius:12px;margin-top:12px;padding:12px}
        .quick h4{margin:0 0 8px;color:#ffd54d}.quick-row{display:flex;justify-content:space-between;padding:8px 2px;border-bottom:1px solid #ffffff16;font-size:13px}
        .temple{margin-top:auto;text-align:center;color:#ffd4a3;font-size:12px;opacity:.85}
        .content{flex:1;min-width:0}
        .topbar{height:70px;background:#fff;border-bottom:1px solid var(--line);display:flex;align-items:center;justify-content:space-between;padding:0 22px;position:sticky;top:0;z-index:5}
        .top-title{font-size:26px;color:var(--maroon);font-weight:800}
        .main{padding:18px;max-width:1500px;margin:auto}
        .stats{display:grid;grid-template-columns:repeat(5,1fr);gap:14px}
        .stat{background:#fff;border:1px solid var(--line);border-radius:12px;padding:18px;box-shadow:0 3px 12px #0000000a}
        .stat .icon{width:45px;height:45px;border-radius:50%;display:grid;place-items:center;font-size:21px;float:left;margin-right:12px}
        .s1{background:#ffe8ed;color:#dc2648}.s2{background:#fff0e2;color:#ee7615}.s3{background:#e7f1ff;color:#1675e8}.s4{background:#e6f8e9;color:#1f9b49}.s5{background:#f1e8ff;color:#8138d7}
        .columns{display:grid;grid-template-columns:1fr 1.2fr;gap:14px;margin-top:14px}
        .card{background:#fff;border:1px solid var(--line);border-radius:12px;padding:18px;box-shadow:0 3px 12px #0000000a}
        .form-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px}
        label{font-weight:700;font-size:13px;display:block;margin-bottom:5px}
        input,select{width:100%;padding:10px;border:1px solid #d5d8df;border-radius:8px;font:inherit;background:#fff}
        .photo-box{height:150px;border:1px dashed #b9bdc6;border-radius:9px;display:grid;place-items:center;color:#777;cursor:pointer;overflow:hidden}
        .actions{display:flex;gap:10px;margin-top:14px}
        .save{background:var(--maroon);color:#fff;flex:1}
        .reset{background:#eef0f3;color:#333;flex:1}
        .note{background:#e8f7e9;color:#26783b;padding:10px;border-radius:7px;margin-top:12px;font-size:13px}
        .searchrow{display:flex;gap:10px;margin-bottom:12px}
        .searchrow input{flex:1}
        .tablewrap{overflow:auto}
        table{width:100%;border-collapse:collapse;min-width:760px}
        th,td{padding:10px 8px;border-bottom:1px solid #eee;text-align:left;font-size:13px}
        th{background:#faf8f6}
        .person{display:flex;gap:9px;align-items:center}
        .avatar{width:72px;height:72px;border-radius:10px;background:#e7d8c8;object-fit:cover;display:grid;place-items:center;color:var(--maroon);font-weight:800;font-size:18px}
        .edit{background:#1675e8;color:#fff;padding:7px 9px}
        .delete{background:#ef3340;color:#fff;padding:7px 9px}
        .report{margin-top:14px}
        .report-grid{display:grid;grid-template-columns:repeat(6,1fr);gap:0;border:1px solid var(--line);border-radius:10px;overflow:hidden}
        .report-box{padding:16px;text-align:center;border-right:1px solid var(--line)}
        .report-box:last-child{border:0}.report-box b{display:block;font-size:25px;margin-top:5px}
        .footer{background:var(--maroon);color:#fff;text-align:center;padding:10px;margin-top:18px}
        @media(max-width:1050px){.stats{grid-template-columns:repeat(3,1fr)}.columns{grid-template-columns:1fr}.report-grid{grid-template-columns:repeat(3,1fr)}}
        @media(max-width:760px){.sidebar{width:72px}.brand div:not(.brand-logo),.navbtn span,.quick,.temple{display:none}.brand{justify-content:center}.navbtn{text-align:center;font-size:0}.navbtn:first-letter{font-size:20px}.top-title{font-size:19px}.stats{grid-template-columns:1fr 1fr}.report-grid{grid-template-columns:1fr 1fr}}
      `}</style>

      <div className="app">
        <aside className="sidebar">
          <div className="brand"><div className="brand-logo">जै</div><div><b>जैन समग्र</b><small>टीम प्रबंधन सिस्टम</small></div></div>
          <button className={`navbtn ${activeView==='dashboard'?'active':''}`} onClick={() => setActiveView('dashboard')}>🏠 <span>डैशबोर्ड</span></button>
          <button className={`navbtn ${activeView==='members'?'active':''}`} onClick={() => setActiveView('members')}>👥 <span>टीम सदस्य</span></button>
          <button className={`navbtn ${activeView==='new'?'active':''}`} onClick={() => setActiveView('new')}>👤 <span>नया सदस्य</span></button>
          <button className={`navbtn ${activeView==='reports'?'active':''}`} onClick={() => setActiveView('reports')}>📊 <span>टीम रिपोर्ट</span></button>
          <button className={`navbtn ${activeView==='settings'?'active':''}`} onClick={() => setActiveView('settings')}>⚙️ <span>सेटिंग</span></button>
          <button className="navbtn" onClick={handleBackup}>💾 <span>डेटा बैकअप</span></button>
          <div className="quick"><h4>त्वरित जानकारी</h4>
            <div className="quick-row"><span>कुल सदस्य</span><b>{stats.total}</b></div>
            <div className="quick-row"><span>राष्ट्रीय स्तर</span><b>{stats.national}</b></div>
            <div className="quick-row"><span>राज्य स्तर</span><b>{stats.state}</b></div>
            <div className="quick-row"><span>संभाग स्तर</span><b>{stats.division}</b></div>
            <div className="quick-row"><span>जिला स्तर</span><b>{stats.district}</b></div>
            <div className="quick-row"><span>मंदिर स्तर</span><b>{stats.temple}</b></div>
          </div>
          <div className="temple">🏛️<br/>© 2026 जैन समग्र<br/>सभी अधिकार सुरक्षित</div>
        </aside>

        <section className="content">
          <header className="topbar"><div className="top-title">जैन समग्र – हमारी टीम</div><div className="top-actions"><button className="btn public" onClick={() => window.open('/about','_blank')}>🌐 टीम पेज देखें</button><button className="btn user" onClick={() => alert('Admin')}>👤 Admin⌄</button></div></header>

          <main className="main">
            {/* Dashboard */}
            {activeView==='dashboard' && (
              <section>
                <div className="stats">
                  <div className="stat"><div className="icon s1">👥</div><small>कुल सदस्य</small><strong>{stats.total}</strong><small>सभी स्तर</small></div>
                  <div className="stat"><div className="icon s2">📍</div><small>राज्य</small><strong>{stats.states}</strong><small>कुल राज्य</small></div>
                  <div className="stat"><div className="icon s3">🏢</div><small>संभाग</small><strong>{stats.division}</strong><small>कुल संभाग</small></div>
                  <div className="stat"><div className="icon s4">🏙️</div><small>जिला</small><strong>{stats.district}</strong><small>कुल जिला</small></div>
                  <div className="stat"><div className="icon s5">🏛️</div><small>मंदिर</small><strong>{stats.temple}</strong><small>कुल मंदिर</small></div>
                </div>

                <div className="columns">
                  <div className="card">
                    <h2>नया सदस्य जोड़ें</h2>
                    <form onSubmit={handleQuickSubmit}>
                      <div className="form-grid">
                        <div><label>नाम *</label><input value={q.name} onChange={e=>handleQuickChange('name',e.target.value)} required placeholder="सदस्य का पूरा नाम"/></div>
                        <div><label>पद *</label><input value={q.post} onChange={e=>handleQuickChange('post',e.target.value)} required placeholder="पद / जिम्मेदारी"/></div>
                        <div><label>मोबाइल *</label><input value={q.phone} onChange={e=>handleQuickChange('phone',e.target.value)} required placeholder="10 अंकों का मोबाइल नंबर"/></div>
                        <div><label>ईमेल</label><input value={q.email} onChange={e=>handleQuickChange('email',e.target.value)} placeholder="ईमेल (वैकल्पिक)"/></div>
                        <div><label>स्तर *</label>
                          <select value={q.level} onChange={e=>handleQuickChange('level',e.target.value)} required>
                            <option value="">स्तर चुनें</option>
                            {LEVELS.map(l=> <option key={l}>{l}</option>)}
                          </select>
                        </div>
                        <div><label>राज्य</label>
                          <select value={q.state} onChange={e=>{handleQuickChange('state',e.target.value); handleQuickChange('division',''); handleQuickChange('district','');}}>
                            <option value="">राज्य चुनें</option>
                            {states.map(s=> <option key={s.id} value={s.name}>{s.name}</option>)}
                          </select>
                        </div>
                        <div><label>संभाग</label>
                          <select value={q.division} onChange={e=>{handleQuickChange('division',e.target.value); handleQuickChange('district','');}} disabled={!q.state}>
                            <option value="">संभाग चुनें</option>
                            {cities.filter(c=>c.state===q.state).map(c=> <option key={c.id} value={c.name}>{c.name}</option>)}
                          </select>
                        </div>
                        <div><label>जिला</label>
                          <select value={q.district} onChange={e=>handleQuickChange('district',e.target.value)} disabled={!q.division}>
                            <option value="">जिला चुनें</option>
                            {blocks.filter(b=>b.city===q.division).map(b=> <option key={b.id} value={b.name}>{b.name}</option>)}
                          </select>
                        </div>
                        <div><label>मंदिर / संगठन</label>
                          <select value={q.temple} onChange={e=>handleQuickChange('temple',e.target.value)} disabled={!q.district}>
                            <option value="">मंदिर / संगठन चुनें</option>
                            {mandirs.filter(m=>m.district===q.district).map(mm=> <option key={mm.id} value={mm.name}>{mm.name}</option>)}
                          </select>
                        </div>
                        <div>
                          <label>फोटो</label>
                          <div className="photo-box" onClick={() => qFileRef.current.click()} style={{cursor:'pointer', position:'relative'}}>
                            {q.photo ? <img src={q.photo} alt="preview" style={{width:'100%',height:'100%',objectFit:'cover'}} /> : 'फोटो चुनें'}
                          </div>
                          <input ref={qFileRef} type="file" accept="image/*" hidden onChange={handleQPhoto} />
                        </div>
                      </div>
                      <div className="actions"><button className="btn save" type="submit">▣ सदस्य सेव करें</button><button type="button" className="btn reset" onClick={()=>setQ({...EMPTY})}>↻ रीसेट करें</button></div>
                      <div className="note">नोट: * वाले फ़ील्ड अनिवार्य हैं।</div>
                    </form>
                  </div>

                  <div className="card">
                    <h2>सदस्य सूची</h2>
                    <div className="searchrow">
                      <input placeholder="🔍 नाम, पद, मोबाइल, स्थान से खोजें..." value={search} onChange={e=>setSearch(e.target.value)} />
                      <select value={filterLevel} onChange={e=>setFilterLevel(e.target.value)}>
                        <option value="">सभी स्तर</option>
                        {LEVELS.map(l=> <option key={l}>{l}</option>)}
                      </select>
                      <button className="btn public" onClick={()=>{setSearch(''); setFilterLevel('');}}>🔄 रीसेट ({filtered.length} मिले)</button>
                    </div>
                    <div className="tablewrap">
                      <table>
                        <thead><tr><th>#</th><th>फोटो</th><th>नाम</th><th>पद</th><th>स्तर / स्थान</th><th>मोबाइल</th><th>कार्यवाही</th></tr></thead>
                        <tbody>
                          {loading ? <tr><td colSpan={7}>लोड हो रहा है...</td></tr> : (filtered.length? filtered.map((m,i)=>(
                            <tr key={m.id}><td>{i+1}</td><td>{m.photo? <img src={m.photo} alt="" className="avatar" style={{cursor:'pointer'}} onClick={()=>openImage && openImage(m.photo)} /> : <div className="avatar">{m.name.split(' ').map(x=>x[0]).join('').slice(0,2)}</div>}</td><td><b>{m.name}</b></td><td>{m.post}</td><td>{m.level}<br/><small>{locationOf(m)}</small></td><td>{m.phone}</td><td>{isAdmin? <><button className="btn edit" onClick={()=>openEdit(m)}>✎</button> <button className="btn delete" onClick={()=>handleDelete(m)}>🗑</button></>: '—'}</td></tr>
                          )): <tr><td colSpan={7}>कोई सदस्य नहीं मिला।</td></tr>)}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="card report">
                  <h2>टीम रिपोर्ट</h2>
                  <div className="form-grid">
                    <div><label>स्तर</label><select id="rLevel" onChange={()=>{}}>{["","राष्ट्रीय स्तर","राज्य स्तर","संभाग स्तर","जिला स्तर","मंदिर स्तर"].map(x=> <option key={x} value={x}>{x||'सभी स्तर'}</option>)}</select></div>
                    <div><label>राज्य</label><select></select></div>
                    <div><label>संभाग</label><select></select></div>
                    <div><label>जिला</label><select></select></div>
                    <div><label>मंदिर / संगठन</label><select></select></div>
                    <div style={{display:'flex',alignItems:'end'}}><button className="btn public" style={{width:'100%'}} onClick={()=>setActiveView('reports')}>📊 रिपोर्ट देखें</button></div>
                  </div>
                  <div className="report-grid" style={{marginTop:14}}>
                    <div className="report-box">कुल सदस्य<b>{stats.total}</b></div>
                    <div className="report-box">राष्ट्रीय स्तर<b style={{color:'#d22'}}>{stats.national}</b></div>
                    <div className="report-box">राज्य स्तर<b style={{color:'#e76f00'}}>{stats.state}</b></div>
                    <div className="report-box">संभाग स्तर<b style={{color:'#1675e8'}}>{stats.division}</b></div>
                    <div className="report-box">जिला स्तर<b style={{color:'#1f9b49'}}>{stats.district}</b></div>
                    <div className="report-box">मंदिर स्तर<b style={{color:'#8138d7'}}>{stats.temple}</b></div>
                  </div>
                </div>
              </section>
            )}

            {/* Members view */}
            {activeView==='members' && (
              <section className="card">
                <h2>👥 सभी टीम सदस्य</h2>
                <div className="searchrow"><input placeholder="नाम / पद / स्थान खोजें..." value={search} onChange={e=>setSearch(e.target.value)} /><select value={filterLevel} onChange={e=>setFilterLevel(e.target.value)}><option value="">सभी स्तर</option>{LEVELS.map(l=> <option key={l}>{l}</option>)}</select></div>
                <div className="tablewrap"><table><thead><tr><th>#</th><th>नाम</th><th>पद</th><th>स्तर</th><th>स्थान</th><th>मोबाइल</th><th>कार्यवाही</th></tr></thead><tbody>{loading? <tr><td colSpan={7}>लोड हो रहा है...</td></tr>: (filtered.length? filtered.map((m,i)=>(<tr key={m.id}><td>{i+1}</td><td>{m.name}</td><td>{m.post}</td><td>{m.level}</td><td>{locationOf(m)}</td><td>{m.phone}</td><td>{isAdmin? <><button className="btn edit" onClick={()=>openEdit(m)}>✎</button> <button className="btn delete" onClick={()=>handleDelete(m)}>🗑</button></>: '—'}</td></tr>)) : <tr><td colSpan={7}>कोई सदस्य नहीं मिला।</td></tr>)}</tbody></table></div>
              </section>
            )}

            {/* New member page uses MemberForm */}
            {activeView==='new' && (
              <div className="card"><h2>➕ नया सदस्य</h2><div className="card-body"><MemberForm initial={EMPTY} states={states} cities={cities} blocks={blocks} mandirs={mandirs} onSave={handleSave} onCancel={()=>{}} saving={saving} openImage={openImage} /></div></div>
            )}

            {/* Reports page */}
            {activeView==='reports' && (
              <div className="card"><h2>📊 विस्तृत टीम रिपोर्ट</h2><div className="card-body">
                {LEVELS.map(level=>{const group=members.filter(m=>m.level===level); if(!group.length) return null; return (<div key={level} className="mb-4"><div className="d-flex align-items-center gap-2 mb-2"><span className={`badge bg-${LEVEL_COLORS[level]||'secondary'} px-3 py-2`}>{level}</span><span className="text-muted fw-semibold" style={{fontSize:13}}>{group.length} सदस्य</span></div><div className="table-responsive"><table className="table table-bordered table-sm mb-0" style={{fontSize:12}}><thead className="table-light"><tr><th>#</th><th>नाम</th><th>पद</th><th>स्थान</th><th>मोबाइल</th><th>ईमेल</th></tr></thead><tbody>{group.map((m,i)=>(<tr key={m.id}><td>{i+1}</td><td className="fw-bold">{m.name}</td><td>{m.post}</td><td>{locationOf(m)}</td><td>{m.phone||'—'}</td><td>{m.email||'—'}</td></tr>))}</tbody></table></div></div>)})}
                {members.length===0 && <div className="text-center py-5 text-muted">कोई सदस्य नहीं मिला।</div>}
              </div></div>
            )}

            {/* Settings view */}
            {activeView==='settings' && (
              <div className="card"><h2>⚙️ सेटिंग</h2><div className="card-body"><form onSubmit={(e)=>{e.preventDefault();alert('Saved settings')}}><div className="form-grid"><div><label>पोर्टल नाम</label><input defaultValue={'जैन समग्र'} /></div><div><label>टीम पेज शीर्षक</label><input defaultValue={'हमारी टीम'} /></div><div><label>विवरण</label><input defaultValue={'राष्ट्रीय से मंदिर स्तर तक टीम विवरण'} /></div><div><label>फुटर स्लोगन</label><input defaultValue={'जैन समग्र – एक कदम, एक समाज, एक संगठन'} /></div></div><div className="actions"><button className="btn save">💾 सेटिंग सेव करें</button></div></form></div></div>
            )}

            {/* Toast */}
            {toast.show && <div className={`toast`} style={{display:'block'}}>{toast.msg}</div>}

            {/* Image viewer modal */}
            {imageViewer && (
              <div onClick={closeImage} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.8)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:3000}}>
                <img src={imageViewer} alt="preview" style={{maxWidth:'95%',maxHeight:'95%',borderRadius:12,boxShadow:'0 8px 40px rgba(0,0,0,0.6)'}}/>
              </div>
            )}

            {/* Edit modal (existing) */}
            {showModal && (
              <>
                <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} onClick={() => { setShowModal(false); setEditMember(null); }} />
                <div className="modal show d-block" style={{ zIndex: 1050 }} tabIndex="-1">
                  <div className="modal-dialog modal-lg modal-dialog-scrollable">
                    <div className="modal-content">
                      <div className="modal-header text-white" style={{ background: "#213F6D" }}>
                        <h5 className="modal-title fw-bold">{editMember ? "✏️ सदस्य संपादित करें" : "➕ नया सदस्य जोड़ें"}</h5>
                        <button className="btn-close btn-close-white" onClick={() => { setShowModal(false); setEditMember(null); }} />
                      </div>
                      <div className="modal-body p-4">
                        <MemberForm initial={editMember || EMPTY} states={states} cities={cities} blocks={blocks} mandirs={mandirs} onSave={handleSave} onCancel={() => { setShowModal(false); setEditMember(null); }} saving={saving} openImage={openImage} />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

          </main>

          <footer className="footer">जैन समग्र – एक कदम, एक समाज, एक संगठन</footer>
        </section>
      </div>
    </div>
  );
};

export default TeamAdmin;
